import { MapPin, Mail, MessageCircle, Clock, Phone } from "lucide-react";
import { site } from "@/data/site";
import { IDS } from "@/constants/testIds";
import { useQuote } from "@/components/site/QuoteDialog";
import Reveal from "@/components/site/Reveal";

export default function Contact() {
  const { open } = useQuote();
  const waLink = `https://wa.me/${site.contact.whatsapp}?text=${encodeURIComponent(
    "Hello Ashrita Overseas, I'd like to enquire about sustainable packaging products."
  )}`;

  return (
    <section
      id="contact"
      data-testid={IDS.contactSection}
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand">
              Contact
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold leading-[1.08] tracking-tight text-ink">
              Let&apos;s talk about your{" "}
              <span className="font-serif-accent italic font-medium text-brand">
                next shipment.
              </span>
            </h2>
            <p className="mt-6 text-mist leading-relaxed">
              Send us a quick enquiry or chat on WhatsApp — we typically reply
              within one business day with a detailed quotation.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => open()}
                data-testid={IDS.contactSendEnquiry}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white hover:bg-forest px-6 py-4 text-sm font-semibold transition-colors duration-300"
              >
                <Mail className="h-4 w-4" /> Send Enquiry
              </button>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid={IDS.contactWhatsapp}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand hover:bg-sage px-6 py-4 text-sm font-semibold transition-colors duration-300"
              >
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: MapPin, t: "Business Address", d: site.contact.address },
                { icon: Mail, t: "Email", d: site.contact.email, href: `mailto:${site.contact.email}` },
                { icon: Phone, t: "WhatsApp", d: site.contact.phone, href: waLink },
                { icon: Clock, t: "Business Hours", d: site.contact.hours },
              ].map((x) => {
                const Wrap = x.href ? "a" : "div";
                return (
                  <Wrap
                    key={x.t}
                    href={x.href}
                    target={x.href?.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className="rounded-2xl border border-line bg-cream/60 p-5 hover:border-brand/30 hover:bg-cream transition-colors duration-300 block"
                  >
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-sage text-forest">
                      <x.icon className="h-4 w-4" />
                    </div>
                    <div className="mt-3 text-xs uppercase tracking-widest text-mist font-semibold">
                      {x.t}
                    </div>
                    <div className="text-sm text-ink mt-0.5 font-medium">
                      {x.d}
                    </div>
                  </Wrap>
                );
              })}
            </div>
          </Reveal>

          {/* Right — map */}
          <Reveal delay={0.1}>
            <div className="relative rounded-3xl overflow-hidden border border-line shadow-xl shadow-forest/10 bg-cream aspect-[4/5]">
              <iframe
                title="Location"
                src={site.contact.map}
                className="absolute inset-0 h-full w-full grayscale-[30%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
